import { useEffect, useState } from "react";
import { Modal, Spinner, ListGroup, Badge, Button } from "react-bootstrap";
import ResizableSplitView from "@components/ResizableSplitView/ResizableSplitView";
import { LaunchNetworkService } from "@services/engagements/launch/launchNetworkService";
import { audienceSizeSummary, type LaunchNetworkLinks, type NetworkLinkState } from "@models/launchNetworkLinks.model";
import { NetworkLinkInspector } from "./NetworkLinkInspector";

interface InspectLinksModalProps {
  show: boolean;
  onClose: () => void;
  network: string;
  launchId: number;
}

const linkKey = (link: NetworkLinkState) => `${link.object_type}:${link.object_id}`;

export const InspectLinksModal = ({ show, onClose, network, launchId }: InspectLinksModalProps) => {
  const [links, setLinks] = useState<LaunchNetworkLinks | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    new LaunchNetworkService()
      .getLinks(launchId, network)
      .then(setLinks)
      .catch((err) => console.error("Failed to load network links:", err))
      .finally(() => setLoading(false));
  }, [launchId, network]);

  const sections: { title: string; items: NetworkLinkState[] }[] = links
    ? [
        { title: "Campaign", items: links.campaign ? [links.campaign] : [] },
        { title: "Ad Groups", items: links.ad_groups },
        { title: "Audiences", items: links.audiences },
        { title: "Ads", items: links.ads },
      ]
    : [];

  const allLinks = sections.flatMap((s) => s.items);
  const selected = allLinks.find((l) => linkKey(l) === selectedKey) ?? null;

  // Apply a saved link back into the flat lists by matching type + id.
  const handleSaved = (updated: NetworkLinkState) => {
    setLinks((prev) => {
      if (!prev) return prev;
      const replace = (l: NetworkLinkState) =>
        l.object_type === updated.object_type && l.object_id === updated.object_id ? updated : l;
      return {
        ...prev,
        campaign: prev.campaign ? replace(prev.campaign) : null,
        ad_groups: prev.ad_groups.map(replace),
        audiences: prev.audiences.map(replace),
        ads: prev.ads.map(replace),
      };
    });
  };

  const handleArchiveAll = async () => {
    setArchiving(true);
    setArchiveError(null);
    const service = new LaunchNetworkService();
    try {
      for (const link of allLinks) {
        const updated = await service.patchLink(launchId, network, link.object_type, link.object_id, {
          status: "PENDING_ARCHIVE",
        });
        handleSaved(updated);
      }
    } catch (err) {
      console.error("Failed to archive links:", err);
      setArchiveError(err instanceof Error ? err.message : "Failed to archive all links.");
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered dialogClassName="modal-90w">
      <Modal.Header closeButton>
        <Modal.Title>Inspect Links — {network}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0" style={{ height: "80vh", overflow: "hidden" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        ) : !links ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <p className="text-muted">Failed to load links.</p>
          </div>
        ) : (
          <ResizableSplitView
            defaultLeftSize={320}
            minLeftSize={240}
            minRightSize={400}
            left={
              <div className="h-100" style={{ overflowY: "auto" }}>
                {sections.map((section) => (
                  <div key={section.title}>
                    <div className="px-3 py-2 bg-light fw-semibold text-uppercase text-muted" style={{ fontSize: "12px" }}>
                      {section.title} ({section.items.length})
                    </div>
                    <ListGroup variant="flush">
                      {section.items.map((item) => (
                        <ListGroup.Item
                          key={linkKey(item)}
                          action
                          active={linkKey(item) === selectedKey}
                          onClick={() => setSelectedKey(linkKey(item))}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <span className="text-truncate me-2">
                            {item.object_name || `#${item.object_id}`}
                            {audienceSizeSummary(item) && (
                              <small className="d-block opacity-75" title="Network-reported size of members uploaded">
                                {audienceSizeSummary(item)}
                              </small>
                            )}
                          </span>
                          <span className="flex-shrink-0">
                            {item.dirty && <Badge bg="warning" className="me-1">dirty</Badge>}
                            <Badge bg={linkKey(item) === selectedKey ? "light" : "secondary"} text={linkKey(item) === selectedKey ? "dark" : undefined}>
                              {item.status}
                            </Badge>
                          </span>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                ))}
              </div>
            }
            right={
              <NetworkLinkInspector
                launchId={launchId}
                network={network}
                link={selected}
                onSaved={handleSaved}
              />
            }
          />
        )}
      </Modal.Body>

      {links && (
        <Modal.Footer className="justify-content-between">
          <span className="text-danger">{archiveError}</span>
          <Button variant="outline-danger" onClick={handleArchiveAll} disabled={archiving}>
            {archiving ? <Spinner size="sm" animation="border" /> : "Archive All"}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};
